import Api from '../api';

export type DocumentRequirementStatusResponse = 'pending' | 'in_review' | 'confirmed' | 'ignored';
export interface ContactResponse {
  id: number;
  name: string | null;
  initials: string | null;
}
export interface DocumentRequirementFileResponse {
  id: number;
  filename: string;
  origin: 'upload' | 'document_manager' | 'analyst' | 'declaration';
  content_type: string | null;
  byte_size: number | null;
  expires_on: string | null;
  created_by: ContactResponse | null;
  created_at: string;
  download_url: string;
  source_document_id?: string | null;
}
export interface AnalystOpinionCandidateResponse {
  id: string;
  nome: string;
  nivel: string | null;
  motivo: string | null;
  origem?: string | null;
  grupo?: string | null;
  situacao?: string | null;
  vencimento?: string | null;
  download_url?: string | null;
}
export interface AnalystOpinionResponse {
  compatibilidade: 'alta' | 'média' | 'revisar';
  recomendado: string | null;
  justificativa: string | null;
  pendencias: string[];
  candidatos: AnalystOpinionCandidateResponse[];
}
export interface DocumentRequirementResponse {
  id: number;
  kind: 'document' | 'declaration';
  source: 'edict' | 'manual';
  status: DocumentRequirementStatusResponse;
  title: string;
  description: string | null;
  edict_excerpt: string | null;
  confidence: string | null;
  position: number;
  created_by: ContactResponse | null;
  confirmed_by: ContactResponse | null;
  confirmed_at: string | null;
  ignored_by: ContactResponse | null;
  ignored_at: string | null;
  analyst_note?: string | null;
  no_document_found?: boolean;
  analyst_opinion?: AnalystOpinionResponse | null;
  files: DocumentRequirementFileResponse[];
}
export interface DocumentRequirementsProgressResponse {
  total: number;
  confirmed: number;
  in_review: number;
  pending: number;
  ignored: number;
  percentage: number;
}
export type DocumentRequirementsAnalysisStatusResponse =
  | 'processing'
  | 'partial'
  | 'done'
  | 'error';
export interface DocumentRequirementsAnalysisResponse {
  status: DocumentRequirementsAnalysisStatusResponse;
  avisos?: string[] | null;
  motivo?: string | null;
}
export interface DocumentRequirementsListResponse {
  requirements: DocumentRequirementResponse[];
  progress: DocumentRequirementsProgressResponse;
  analyzed: boolean;
  analysis?: DocumentRequirementsAnalysisResponse | null;
}
export interface DocumentRequirementMutationResponse {
  requirement: DocumentRequirementResponse;
  progress: DocumentRequirementsProgressResponse;
}
export interface DocumentManagerDocumentResponse {
  id: string;
  provider: 'aws' | 'idocumentei';
  name: string;
  group: string | null;
  group_id: number | null;
  cnpj: string;
  expires_on: string | null;
  expired: boolean;
  status_label: string;
}

export interface NewDocumentRequirementPayload {
  title: string;
  description?: string;
  edict_excerpt?: string;
}

const basePath = (biddingId: number) => `/gestao_ia/licitacoes/${biddingId}/exigencias_documentos`;

export const listDocumentRequirements = (biddingId: number) =>
  Api.get<DocumentRequirementsListResponse>(basePath(biddingId));

export const analyzeDocumentRequirements = (biddingId: number) =>
  Api.post<DocumentRequirementsListResponse>(`${basePath(biddingId)}/analisar`);

export const createDocumentRequirement = (
  biddingId: number,
  payload: NewDocumentRequirementPayload,
) => Api.post<DocumentRequirementMutationResponse>(basePath(biddingId), payload);

export const ignoreDocumentRequirement = (biddingId: number, requirementId: number) =>
  Api.patch<DocumentRequirementMutationResponse>(`${basePath(biddingId)}/${requirementId}/ignorar`);

export const restoreDocumentRequirement = (biddingId: number, requirementId: number) =>
  Api.patch<DocumentRequirementMutationResponse>(
    `${basePath(biddingId)}/${requirementId}/restaurar`,
  );

export const uploadDocumentRequirementFile = (
  biddingId: number,
  requirementId: number,
  payload: { file: File; name: string; expiresOn?: string },
) => {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('name', payload.name);
  if (payload.expiresOn) formData.append('expires_on', payload.expiresOn);

  return Api.post<DocumentRequirementMutationResponse>(
    `${basePath(biddingId)}/${requirementId}/arquivos`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

export interface ImportDocumentOptions {
  replace?: boolean;
}

export const importDocumentRequirementFile = (
  biddingId: number,
  requirementId: number,
  documentId: string,
  options: ImportDocumentOptions = {},
) =>
  Api.post<DocumentRequirementMutationResponse>(
    `${basePath(biddingId)}/${requirementId}/arquivos`,
    {
      document_id: documentId,
      ...(options.replace ? { replace: true } : {}),
    },
  );

export const removeDocumentRequirementFile = (
  biddingId: number,
  requirementId: number,
  fileId: number,
) =>
  Api.delete<DocumentRequirementMutationResponse>(
    `${basePath(biddingId)}/${requirementId}/arquivos/${fileId}`,
  );

export const listDocumentManagerDocuments = (biddingId: number) =>
  Api.get<{ documents: DocumentManagerDocumentResponse[] }>(
    `${basePath(biddingId)}/gerenciar_documentos`,
  );
